import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { useCanEdit } from '@/auth/account';
import { isHttpUrl, linkCaption, normalizeUrl } from '@/lib/links';
import { OpenLink } from './LinkList';
import type { Position } from '@/api/types';
import {
  useCreatePosition,
  useDeletePosition,
  useReorderPositions,
  useUpdatePosition,
} from '@/api/hooks';
import { centsToInput, formatMoney, parseMoneyToCents, parseQuantity } from '@/lib/format';
import { Button } from './ui/Button';
import { TextInput } from './ui/Field';
import { SortableList, SortableRow } from './SortableList';

interface PositionEditorProps {
  investmentId: string;
  positions: Position[];
  currency: string;
}

/**
 * Inline table of line items. Each field commits on blur so the totals update
 * without an explicit save, matching how a spreadsheet behaves.
 */
export function PositionEditor({ investmentId, positions, currency }: PositionEditorProps) {
  const canEdit = useCanEdit();
  const [order, setOrder] = useState<string[] | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ label: '', quantity: '1', unit: '', unitPrice: '' });

  const createPosition = useCreatePosition();
  const updatePosition = useUpdatePosition();
  const deletePosition = useDeletePosition();
  const reorderPositions = useReorderPositions();

  // Show the optimistic order while the reorder request is in flight.
  const ordered =
    order === null
      ? positions
      : (order
          .map((id) => positions.find((position) => position.id === id))
          .filter(Boolean) as Position[]);

  const total = positions.reduce((sum, position) => sum + position.total, 0);

  const handleReorder = (ids: string[]) => {
    setOrder(ids);
    reorderPositions.mutate({ investmentId, ids }, { onSettled: () => setOrder(null) });
  };

  const addPosition = () => {
    const label = draft.label.trim();
    if (label === '') return;
    createPosition.mutate(
      {
        investmentId,
        label,
        quantity: parseQuantity(draft.quantity) ?? 1,
        unit: draft.unit.trim() === '' ? null : draft.unit.trim(),
        unitPrice: parseMoneyToCents(draft.unitPrice) ?? 0,
      },
      { onSuccess: () => setDraft({ label: '', quantity: '1', unit: '', unitPrice: '' }) },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="hidden gap-2 px-7 text-[11px] font-medium text-[var(--ink-3)] sm:grid sm:grid-cols-[1fr_4.5rem_4rem_7rem_6rem_2rem]">
        <span>Bezeichnung</span>
        <span className="text-right">Menge</span>
        <span>Einheit</span>
        <span className="text-right">Einzelpreis</span>
        <span className="text-right">Summe</span>
        <span />
      </div>

      {ordered.length > 0 ? (
        <SortableList items={ordered} onReorder={handleReorder} className="gap-2">
          {(position) => (
            <SortableRow key={position.id} id={position.id} handleLabel={position.label}>
              <div className="grid items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-2 sm:grid-cols-[1fr_4.5rem_4rem_7rem_6rem_2rem] sm:border-transparent sm:bg-transparent sm:p-0">
                <TextInput
                  aria-label={`Bezeichnung von ${position.label}`}
                  defaultValue={position.label}
                  onBlur={(event) => {
                    const label = event.target.value.trim();
                    if (label !== '' && label !== position.label) {
                      updatePosition.mutate({ id: position.id, label });
                    } else {
                      event.target.value = position.label;
                    }
                  }}
                />
                <TextInput
                  aria-label={`Menge von ${position.label}`}
                  inputMode="decimal"
                  className="num text-right"
                  defaultValue={String(position.quantity)}
                  onBlur={(event) => {
                    const quantity = parseQuantity(event.target.value);
                    if (quantity !== null && quantity !== position.quantity) {
                      updatePosition.mutate({ id: position.id, quantity });
                    } else {
                      event.target.value = String(position.quantity);
                    }
                  }}
                />
                <TextInput
                  aria-label={`Einheit von ${position.label}`}
                  placeholder="Stk"
                  defaultValue={position.unit ?? ''}
                  onBlur={(event) => {
                    const unit = event.target.value.trim();
                    if (unit !== (position.unit ?? '')) {
                      updatePosition.mutate({ id: position.id, unit: unit === '' ? null : unit });
                    }
                  }}
                />
                <TextInput
                  aria-label={`Einzelpreis von ${position.label}`}
                  inputMode="decimal"
                  className="num text-right"
                  defaultValue={centsToInput(position.unitPrice)}
                  onBlur={(event) => {
                    const unitPrice = parseMoneyToCents(event.target.value) ?? 0;
                    if (unitPrice !== position.unitPrice) {
                      updatePosition.mutate({ id: position.id, unitPrice });
                    } else {
                      event.target.value = centsToInput(position.unitPrice);
                    }
                  }}
                />
                <span className="num px-1 text-right text-sm font-medium tabular-nums">
                  {formatMoney(position.total, currency)}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`${position.label} löschen`}
                  onClick={() => deletePosition.mutate(position.id)}
                  className="h-8 w-8 justify-self-end hover:text-[var(--critical)]"
                >
                  <Trash2 size={14} />
                </Button>
                {/* Second line: optional link to the product / offer of this position. */}
                <div className="flex items-center gap-2 sm:col-span-6">
                  <TextInput
                    aria-label={`Link von ${position.label}`}
                    placeholder="Link (optional), z. B. Produktseite"
                    inputMode="url"
                    className="h-8 text-xs"
                    defaultValue={position.url ?? ''}
                    onBlur={(event) => {
                      const url = normalizeUrl(event.target.value);
                      if (url === (position.url ?? '')) return;
                      if (url !== '' && !isHttpUrl(url)) {
                        setUrlError(`„${event.target.value}“ ist kein gültiger Link.`);
                        return;
                      }
                      setUrlError(null);
                      event.target.value = url;
                      updatePosition.mutate({ id: position.id, url: url === '' ? null : url });
                    }}
                  />
                  {position.url ? <OpenLink url={position.url} label={linkCaption(position.label, position.url)} /> : <span className="w-8 shrink-0" />}
                </div>
              </div>
            </SortableRow>
          )}
        </SortableList>
      ) : (
        <p className="px-7 text-xs text-[var(--ink-3)]">
          Noch keine Positionen. Solange keine erfasst sind, zählt die Kostenschätzung.
        </p>
      )}

      {urlError ? (
        <p role="alert" className="px-7 text-xs text-[var(--critical)]">
          {urlError} Bitte mit http:// oder https:// angeben.
        </p>
      ) : null}

      {canEdit ? (
      <div className="grid items-center gap-2 rounded-lg border border-dashed border-[var(--border-strong)] p-2 sm:ml-7 sm:grid-cols-[1fr_4.5rem_4rem_7rem_6rem_2rem]">
        <TextInput
          aria-label="Neue Position"
          placeholder="Neue Position …"
          value={draft.label}
          onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addPosition();
            }
          }}
        />
        <TextInput
          aria-label="Menge"
          inputMode="decimal"
          className="num text-right"
          value={draft.quantity}
          onChange={(event) => setDraft({ ...draft, quantity: event.target.value })}
        />
        <TextInput
          aria-label="Einheit"
          placeholder="Stk"
          value={draft.unit}
          onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
        />
        <TextInput
          aria-label="Einzelpreis"
          inputMode="decimal"
          placeholder="0,00"
          className="num text-right"
          value={draft.unitPrice}
          onChange={(event) => setDraft({ ...draft, unitPrice: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addPosition();
            }
          }}
        />
        <Button
          size="sm"
          variant="primary"
          onClick={addPosition}
          disabled={draft.label.trim() === '' || createPosition.isPending}
          className="sm:col-span-2 sm:justify-self-end"
        >
          <Plus size={14} />
          Hinzufügen
        </Button>
      </div>
      ) : null}

      {positions.length > 0 ? (
        <div className="flex items-baseline justify-between border-t border-[var(--border)] px-1 pt-3 sm:ml-7">
          <span className="text-xs font-medium text-[var(--ink-2)]">
            Summe aus {positions.length} Position{positions.length === 1 ? '' : 'en'}
          </span>
          <span className="num text-base font-semibold">{formatMoney(total, currency)}</span>
        </div>
      ) : null}
    </div>
  );
}
